import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Edit, Trash2, Plus, KeyRound, Copy, ExternalLink, Sparkles, Eye } from 'lucide-react';
import ModuleCard from './ModuleCard';
import RevisionMeta from './RevisionMeta';
import CoursePreviewModal from './CoursePreviewModal';
import { stripAndTruncate } from '../../../lib/richText';

const VISIBILITY_STYLES = {
  public:   { cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200', label: 'PUBLIC',   tip: 'Public — listed in the catalogue for every member.' },
  unlisted: { cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',         label: 'UNLISTED', tip: 'Unlisted — hidden from the catalogue but accessible via direct link.' },
  private:  { cls: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',             label: 'PRIVATE',  tip: 'Private — invited members only. Requires an access code to enroll.' },
};

const CourseCard = ({
 course,
 isExpanded,
 details,
 tierColors,
 expandedModules,
 onToggle,
 onEdit,
 onDelete,
 onAddModule,
 onToggleModule,
 onEditModule,
 onDeleteModule,
 onAddLesson,
 onEditLesson,
 onDeleteLesson,
 onBatchGenerated,
 onFillEmptyLessons,
 lessonForm,
 setLessonForm,
 setShowLessonForm
}) => {
 const { API } = useContext(AuthContext);
 const navigate = useNavigate();
 const [mintingCode, setMintingCode] = useState(false);
 const [fillingEmpty, setFillingEmpty] = useState(false);
 const [showPreview, setShowPreview] = useState(false);

 const visibility = (course.visibility || 'public').toLowerCase();
 const vStyle = VISIBILITY_STYLES[visibility] || VISIBILITY_STYLES.public;
 const needsCode = visibility === 'private' || course.access_code_required;

 // Mint a single-use access code that unlocks this specific course. We
 // reuse the existing content-codes API — target_type='course' — so all
 // codes flow through the same audit trail on /admin/codes.
 const generateAccessCode = async () => {
 setMintingCode(true);
 try {
 const { data } = await axios.post(`${API}/content-codes/generate`, {
 target_type:      'course',
 target_id:        course.id,
 target_label:     course.title,
 count:            1,
 grant_days:       365,
 max_redemptions:  1,
 note:             `Minted from LMS admin for "${course.title}"`,
 });
 const code = data?.codes?.[0]?.code;
 if (!code) throw new Error('Backend returned no code');
 try {
 await navigator.clipboard.writeText(code);
 toast.success(`Code ${code} copied to clipboard — valid 365 days.`);
 } catch {
 // Fallback: window.prompt so admin can copy manually if clipboard
 // permissions blocked (Safari, embedded contexts).
 window.prompt('Copy this access code:', code);
 toast.success(`Code ${code} generated.`);
 }
 } catch (e) {
 const msg = e?.response?.data?.detail || e?.message || 'Failed to mint code';
 toast.error(String(msg));
 } finally {
 setMintingCode(false);
 }
 };

 return (
 <Card className={isExpanded ? 'border-2 border-blue-500' : ''}>
 <CardHeader>
 <div className="flex items-start justify-between">
 <div className="flex-1">
 <div className="flex items-center gap-3 mb-2">
 <span className={`px-3 py-1 rounded-full text-xs font-semibold ${tierColors[course.tier]}`}>
 {course.tier.toUpperCase()}
 </span>
 {!course.is_published && (
 <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
 DRAFT
 </span>
 )}
 <span
 className={`px-3 py-1 rounded-full text-xs font-semibold ${vStyle.cls}`}
 data-testid={`course-visibility-pill-${course.id}`}
 title={vStyle.tip}
 >
 {vStyle.label}
 </span>
 {course.ad_enabled && (
 <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
 📢 AD ENABLED
 </span>
 )}
 </div>
 <CardTitle>{course.title}</CardTitle>
 <CardDescription>{stripAndTruncate(course.description, 200)}</CardDescription>
 <p className="text-sm text-slate-500 dark:text-neutral-500 mt-2">
 {course.modules_count || 0} modules • {course.total_lessons || 0} lessons
 </p>
 <RevisionMeta
 className="mt-2"
 createdAt={course.created_at}
 publishedAt={course.published_at}
 reviewBy={course.review_by}
 testIdPrefix={`course-revision-${course.id}`}
 />
 </div>
 <div className="flex gap-2">
 <Button
 size="sm"
 onClick={() => navigate(`/admin/lms/course/${course.id}/workspace`)}
 className="bg-primary hover:bg-primary/90"
 title="Open the full course workspace — sidebar navigation, no scroll-to-top"
 data-testid={`course-open-workspace-${course.id}`}
 >
 <ExternalLink className="w-4 h-4 mr-1" />
 Open workspace
 </Button>
 <Button
 size="sm"
 variant="outline"
 onClick={() => setShowPreview(true)}
 title="Preview the course exactly as a learner sees it — inline modal"
 data-testid={`course-preview-btn-${course.id}`}
 >
 <Eye className="w-4 h-4 mr-1" />
 Preview
 </Button>
 <Button
 size="sm"
 variant="outline"
 onClick={onToggle}
 title="Quick peek — expand inline (or use Open workspace for the full editor)"
 >
 {isExpanded ? 'Collapse' : 'Peek'}
 </Button>
 {needsCode && (
 <Button
 size="sm"
 variant="outline"
 onClick={generateAccessCode}
 disabled={mintingCode}
 title="Mint a 1-use access code that unlocks this course for 365 days"
 data-testid={`course-mint-code-${course.id}`}
 >
 <KeyRound className="w-4 h-4 mr-1 text-amber-500" />
 {mintingCode ? '…' : 'Generate code'}
 </Button>
 )}
 <Button
 size="sm"
 variant="outline"
 onClick={() => navigate(`/admin/lms/course/${course.id}/workspace`)}
 title="Edit course details (opens workspace)"
 >
 <Edit className="w-4 h-4" />
 </Button>
 {onFillEmptyLessons && (
 <Button
 size="sm"
 variant="outline"
 onClick={async () => {
 setFillingEmpty(true);
 try { await onFillEmptyLessons(course); }
 finally { setFillingEmpty(false); }
 }}
 disabled={fillingEmpty}
 title="Queue AI content generation for every lesson in this course that still has no body / quiz"
 className="border-amber-400 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20"
 data-testid={`course-fill-empty-${course.id}`}
 >
 <Sparkles className="w-4 h-4 mr-1" /> {fillingEmpty ? 'Queueing…' : 'Fill empty lessons'}
 </Button>
 )}
 <Button
 size="sm"
 variant="ghost"
 onClick={onDelete}
 className="text-red-600"
 title="Delete course"
 >
 <Trash2 className="w-4 h-4" />
 </Button>
 </div>
 </div>
 </CardHeader>
 
 {/* Expanded Content */}
 {isExpanded && details && (
 <CardContent className="border-t">
 {/* Add Module Button for this Course */}
 <div className="flex justify-end mb-4 pt-4">
 <Button
 size="sm"
 onClick={onAddModule}
 className="bg-green-600 hover:bg-green-700"
 >
 <Plus className="w-4 h-4 mr-2" />
 Add Module to this Course
 </Button>
 </div>
 
 <div className="space-y-4">
 {details.modules && details.modules.length > 0 ? (
 details.modules.map((module, moduleIndex) => (
 <ModuleCard
 key={module.id}
 module={module}
 moduleIndex={moduleIndex}
 isExpanded={expandedModules[module.id]}
 onToggle={() => onToggleModule(module.id)}
 onEdit={() => onEditModule(module)}
 onDelete={() => onDeleteModule(module.id)}
 onAddLesson={() => {
 setLessonForm({ ...lessonForm, module_id: module.id });
 setShowLessonForm(true);
 window.scrollTo({ top: 0, behavior: 'smooth' });
 }}
 onEditLesson={onEditLesson}
 onDeleteLesson={onDeleteLesson}
 onBatchGenerated={onBatchGenerated}
 />
 ))
 ) : (
 <p className="text-slate-600 dark:text-neutral-400 text-center py-4">
 No modules yet. Add modules using the button above.
 </p>
 )}
 </div>
 </CardContent>
 )}
 <CoursePreviewModal
   open={showPreview}
   onOpenChange={setShowPreview}
   courseId={course.id}
   courseTitle={course.title}
 />
 </Card>
 );
};

export default CourseCard;
